import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { statisticsService, OrdersPerDayResponse, ClientsPerDayResponse, DailyPriceStatisticsResponse, DailyRestaurantStatisticsResponse, OrderedClientsStatisticsResponse, DriverOrderStatisticsResponse } from '../../services/statisticsService';
import { restaurantsService } from '../../services/restaurantsService';
import { RestaurantResponse } from '../../types/restaurants';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './Page.css';

type DateRangePreset = 'today' | 'last7days' | 'thisWeek' | 'thisMonth' | 'last30days' | 'custom';

const Statistics: React.FC = () => {
  const { t } = useTranslation();

  // Orders statistics state
  const [data, setData] = useState<OrdersPerDayResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('thisMonth');
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');

  // Clients statistics state
  const [clientsData, setClientsData] = useState<ClientsPerDayResponse[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [clientsSelectedPreset, setClientsSelectedPreset] = useState<DateRangePreset>('thisMonth');
  const [clientsCustomFromDate, setClientsCustomFromDate] = useState('');
  const [clientsCustomToDate, setClientsCustomToDate] = useState('');

  // Ordered clients statistics state
  const [orderedClientsData, setOrderedClientsData] = useState<OrderedClientsStatisticsResponse[]>([]);
  const [orderedClientsLoading, setOrderedClientsLoading] = useState(false);
  const [orderedClientsError, setOrderedClientsError] = useState('');
  const [orderedClientsSelectedPreset, setOrderedClientsSelectedPreset] = useState<DateRangePreset>('thisMonth');
  const [orderedClientsCustomFromDate, setOrderedClientsCustomFromDate] = useState('');
  const [orderedClientsCustomToDate, setOrderedClientsCustomToDate] = useState('');

  // Prices statistics state
  const [pricesData, setPricesData] = useState<DailyPriceStatisticsResponse[]>([]);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [pricesError, setPricesError] = useState('');
  const [pricesSelectedPreset, setPricesSelectedPreset] = useState<DateRangePreset>('thisMonth');
  const [pricesCustomFromDate, setPricesCustomFromDate] = useState('');
  const [pricesCustomToDate, setPricesCustomToDate] = useState('');

  // Restaurant statistics state
  const [restaurantData, setRestaurantData] = useState<DailyRestaurantStatisticsResponse[]>([]);
  const [restaurantLoading, setRestaurantLoading] = useState(false);
  const [restaurantError, setRestaurantError] = useState('');
  const [restaurantSelectedPreset, setRestaurantSelectedPreset] = useState<DateRangePreset>('thisMonth');
  const [restaurantCustomFromDate, setRestaurantCustomFromDate] = useState('');
  const [restaurantCustomToDate, setRestaurantCustomToDate] = useState('');

  // Restaurants list state
  const [restaurants, setRestaurants] = useState<RestaurantResponse[]>([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(false);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>('');

  // Driver statistics state
  const [driverData, setDriverData] = useState<DriverOrderStatisticsResponse[]>([]);
  const [driverLoading, setDriverLoading] = useState(false);
  const [driverError, setDriverError] = useState('');
  const [driverSelectedPreset, setDriverSelectedPreset] = useState<DateRangePreset>('thisMonth');
  const [driversCount, setDriversCount] = useState<number>(10);
  const [driverCustomFromDate, setDriverCustomFromDate] = useState('');
  const [driverCustomToDate, setDriverCustomToDate] = useState('');

  const getDateRange = (preset: DateRangePreset, customFrom?: string, customTo?: string): { from: string; to: string } => {
    const now = new Date();

    // Create ISO-8601 date strings with timezone (start of day)
    const getStartOfDay = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      // Get timezone offset in minutes and convert to +HH:MM format
      const offset = -date.getTimezoneOffset();
      const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
      const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');
      const offsetSign = offset >= 0 ? '+' : '-';
      const timezone = `${offsetSign}${offsetHours}:${offsetMinutes}`;

      return `${year}-${month}-${day}T00:00:00${timezone}`;
    };

    // Create ISO-8601 date strings with timezone (end of day)
    const getEndOfDay = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      // Get timezone offset in minutes and convert to +HH:MM format
      const offset = -date.getTimezoneOffset();
      const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
      const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');
      const offsetSign = offset >= 0 ? '+' : '-';
      const timezone = `${offsetSign}${offsetHours}:${offsetMinutes}`;

      return `${year}-${month}-${day}T23:59:59${timezone}`;
    };

    // Handle custom date range
    if (preset === 'custom' && customFrom && customTo) {
      // Get current timezone offset for consistency
      const now = new Date();
      const offset = -now.getTimezoneOffset();
      const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
      const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');
      const offsetSign = offset >= 0 ? '+' : '-';
      const timezone = `${offsetSign}${offsetHours}:${offsetMinutes}`;

      // customFrom and customTo are in YYYY-MM-DD format
      return {
        from: `${customFrom}T00:00:00${timezone}`,
        to: `${customTo}T23:59:59${timezone}`
      };
    }

    let fromDate: string;
    let toDate: string = getEndOfDay(now);

    switch (preset) {
      case 'custom':
        // If custom is selected but dates are not provided, use today
        fromDate = getStartOfDay(now);
        break;
      case 'today':
        fromDate = getStartOfDay(now);
        break;
      case 'last7days':
        // Last 7 days including today
        const last7Start = new Date(now);
        last7Start.setDate(last7Start.getDate() - 6);
        fromDate = getStartOfDay(last7Start);
        break;
      case 'thisWeek':
        // This week starting from Monday
        const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday, go back 6 days; else go back (day - 1)
        const monday = new Date(now);
        monday.setDate(monday.getDate() - daysFromMonday);
        fromDate = getStartOfDay(monday);
        break;
      case 'thisMonth':
        // First day of current month
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        fromDate = getStartOfDay(firstDay);
        break;
      case 'last30days':
        // Last 30 days including today
        const last30Start = new Date(now);
        last30Start.setDate(last30Start.getDate() - 29);
        fromDate = getStartOfDay(last30Start);
        break;
    }

    return {
      from: fromDate,
      to: toDate
    };
  };

  const fetchStatistics = async (preset: DateRangePreset, customFrom?: string, customTo?: string) => {
    setLoading(true);
    setError('');

    try {
      const dateRange = getDateRange(preset, customFrom, customTo);
      const response = await statisticsService.getOrdersPerDay(dateRange);

      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.error || 'Failed to fetch statistics');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientsStatistics = async (preset: DateRangePreset, customFrom?: string, customTo?: string) => {
    setClientsLoading(true);
    setClientsError('');

    try {
      const dateRange = getDateRange(preset, customFrom, customTo);
      const response = await statisticsService.getClientsPerDay(dateRange);

      if (response.success && response.data) {
        setClientsData(response.data);
      } else {
        setClientsError(response.error || 'Failed to fetch clients statistics');
      }
    } catch (error) {
      setClientsError('Network error. Please try again.');
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchOrderedClientsStatistics = async (preset: DateRangePreset, customFrom?: string, customTo?: string) => {
    setOrderedClientsLoading(true);
    setOrderedClientsError('');

    try {
      const dateRange = getDateRange(preset, customFrom, customTo);
      const response = await statisticsService.getOrderedClients(dateRange);

      if (response.success && response.data) {
        setOrderedClientsData(response.data);
      } else {
        setOrderedClientsError(response.error || 'Failed to fetch ordered clients statistics');
      }
    } catch (error) {
      setOrderedClientsError('Network error. Please try again.');
    } finally {
      setOrderedClientsLoading(false);
    }
  };

  const fetchPricesStatistics = async (preset: DateRangePreset, customFrom?: string, customTo?: string) => {
    setPricesLoading(true);
    setPricesError('');

    try {
      const dateRange = getDateRange(preset, customFrom, customTo);
      const response = await statisticsService.getPricesPerDay(dateRange);

      if (response.success && response.data) {
        setPricesData(response.data);
      } else {
        setPricesError(response.error || 'Failed to fetch prices statistics');
      }
    } catch (error) {
      setPricesError('Network error. Please try again.');
    } finally {
      setPricesLoading(false);
    }
  };

  const fetchRestaurants = async () => {
    setRestaurantsLoading(true);

    try {
      const response = await restaurantsService.getRestaurants({
        searchQuery: '',
        page: 0,
        pageSize: 100,
        applicationStatus: null
      });

      if (response.success && response.data) {
        setRestaurants(response.data);
        // Auto-select first restaurant if available
        if (response.data.length > 0 && !selectedRestaurantId) {
          setSelectedRestaurantId(response.data[0].id.toString());
        }
      }
    } catch (error) {
      console.error('Failed to fetch restaurants');
    } finally {
      setRestaurantsLoading(false);
    }
  };

  const fetchRestaurantStatistics = async (preset: DateRangePreset, restaurantId: string, customFrom?: string, customTo?: string) => {
    if (!restaurantId) return;

    setRestaurantLoading(true);
    setRestaurantError('');

    try {
      const dateRange = getDateRange(preset, customFrom, customTo);
      const response = await statisticsService.getRestaurantPerDay({
        ...dateRange,
        restaurantId
      });

      if (response.success && response.data) {
        setRestaurantData(response.data);
      } else {
        setRestaurantError(response.error || 'Failed to fetch restaurant statistics');
      }
    } catch (error) {
      setRestaurantError('Network error. Please try again.');
    } finally {
      setRestaurantLoading(false);
    }
  };

  const fetchDriverStatistics = async (preset: DateRangePreset, count: number, customFrom?: string, customTo?: string) => {
    setDriverLoading(true);
    setDriverError('');

    try {
      const dateRange = getDateRange(preset, customFrom, customTo);
      const response = await statisticsService.getDriverOrderStatistics({
        ...dateRange,
        driversCount: count
      });

      if (response.success && response.data) {
        setDriverData(response.data);
      } else {
        setDriverError(response.error || 'Failed to fetch driver statistics');
      }
    } catch (error) {
      setDriverError('Network error. Please try again.');
    } finally {
      setDriverLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics(selectedPreset, customFromDate, customToDate);
  }, [selectedPreset, customFromDate, customToDate]);

  useEffect(() => {
    fetchClientsStatistics(clientsSelectedPreset, clientsCustomFromDate, clientsCustomToDate);
  }, [clientsSelectedPreset, clientsCustomFromDate, clientsCustomToDate]);

  useEffect(() => {
    fetchOrderedClientsStatistics(orderedClientsSelectedPreset, orderedClientsCustomFromDate, orderedClientsCustomToDate);
  }, [orderedClientsSelectedPreset, orderedClientsCustomFromDate, orderedClientsCustomToDate]);

  useEffect(() => {
    fetchPricesStatistics(pricesSelectedPreset, pricesCustomFromDate, pricesCustomToDate);
  }, [pricesSelectedPreset, pricesCustomFromDate, pricesCustomToDate]);

  useEffect(() => {
    fetchRestaurants();
  }, []);

  useEffect(() => {
    if (selectedRestaurantId) {
      fetchRestaurantStatistics(restaurantSelectedPreset, selectedRestaurantId, restaurantCustomFromDate, restaurantCustomToDate);
    }
  }, [restaurantSelectedPreset, selectedRestaurantId, restaurantCustomFromDate, restaurantCustomToDate]);

  useEffect(() => {
    fetchDriverStatistics(driverSelectedPreset, driversCount, driverCustomFromDate, driverCustomToDate);
  }, [driverSelectedPreset, driversCount, driverCustomFromDate, driverCustomToDate]);

  const handlePresetChange = (preset: DateRangePreset) => {
    setSelectedPreset(preset);
  };

  const handleClientsPresetChange = (preset: DateRangePreset) => {
    setClientsSelectedPreset(preset);
  };

  const handleOrderedClientsPresetChange = (preset: DateRangePreset) => {
    setOrderedClientsSelectedPreset(preset);
  };

  const handlePricesPresetChange = (preset: DateRangePreset) => {
    setPricesSelectedPreset(preset);
  };

  const handleRestaurantPresetChange = (preset: DateRangePreset) => {
    setRestaurantSelectedPreset(preset);
  };

  const handleRestaurantChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRestaurantId(event.target.value);
  };

  const handleDriverPresetChange = (preset: DateRangePreset) => {
    setDriverSelectedPreset(preset);
  };

  const handleDriversCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    if (value > 0) {
      setDriversCount(value);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTotalOrders = () => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, item) => sum + (item.completedOrdersCount || 0), 0);
  };

  const getTotalProblematicallyCompleted = () => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, item) => sum + (item.problematicallyCompletedOrdersCount || 0), 0);
  };

  const getTotalCancelled = () => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, item) => sum + (item.cancelledOrdersCount || 0), 0);
  };

  const getAverageOrders = () => {
    if (!data || data.length === 0) return 0;
    const total = getTotalOrders();
    return Math.round(total / data.length) || 0;
  };

  const getPresetLabel = (preset: DateRangePreset): string => {
    switch (preset) {
      case 'today':
        return t('statistics.today');
      case 'last7days':
        return t('statistics.last7days');
      case 'thisWeek':
        return t('statistics.thisWeek');
      case 'thisMonth':
        return t('statistics.thisMonth');
      case 'last30days':
        return t('statistics.last30days');
      case 'custom':
        return t('statistics.customRange');
    }
  };

  // Prepare orders chart data
  const chartData = (data || []).map(item => ({
    date: formatDate(item.date),
    orders: item.completedOrdersCount || 0,
    problematicallyCompleted: item.problematicallyCompletedOrdersCount || 0,
    cancelled: item.cancelledOrdersCount || 0
  }));

  // Prepare clients chart data (cumulative counts)
  const clientsChartData = (clientsData || []).map(item => ({
    date: formatDate(item.date),
    ios: item.iosClientsCount || 0,
    android: item.androidClientsCount || 0,
    total: (item.iosClientsCount || 0) + (item.androidClientsCount || 0)
  }));

  // Prepare ordered clients chart data
  const orderedClientsChartData = (orderedClientsData || []).map(item => ({
    date: formatDate(item.date),
    allOrdered: item.allOrderedClientsCount || 0,
    orderedTwice: item.clientsOrderedTwiceCount || 0,
    orderedMoreThanTwice: item.clientsOrderedMoreThanTwiceCount || 0
  }));

  const getTotalClients = () => {
    if (!clientsData || clientsData.length === 0) return 0;
    // Return the last value as it represents the total cumulative count
    const last = clientsData[clientsData.length - 1];
    return (last.iosClientsCount || 0) + (last.androidClientsCount || 0);
  };

  const getTotalIOSClients = () => {
    if (!clientsData || clientsData.length === 0) return 0;
    // Return the last value as it represents the total cumulative iOS count
    return clientsData[clientsData.length - 1]?.iosClientsCount || 0;
  };

  const getTotalAndroidClients = () => {
    if (!clientsData || clientsData.length === 0) return 0;
    // Return the last value as it represents the total cumulative Android count
    return clientsData[clientsData.length - 1]?.androidClientsCount || 0;
  };

  const getNewClients = () => {
    if (!clientsData || clientsData.length === 0) return 0;
    if (clientsData.length === 1) {
      const first = clientsData[0];
      return (first.iosClientsCount || 0) + (first.androidClientsCount || 0);
    }
    // Calculate difference between last and first to get new clients in the period
    const first = clientsData[0];
    const last = clientsData[clientsData.length - 1];
    const firstTotal = (first.iosClientsCount || 0) + (first.androidClientsCount || 0);
    const lastTotal = (last.iosClientsCount || 0) + (last.androidClientsCount || 0);
    return lastTotal - firstTotal;
  };

  const getAverageClientsPerDay = () => {
    if (!clientsData || clientsData.length === 0) return 0;
    return Math.round(getNewClients() / clientsData.length);
  };

  // Prepare prices chart data
  const pricesChartData = (pricesData || []).map(item => ({
    date: formatDate(item.date),
    foodPrice: item.totalFoodPrice || 0,
    deliveryPrice: item.totalDeliveryPrice || 0,
    profit: item.totalPlatformProfit || 0
  }));

  const getTotalFoodPrice = () => {
    if (!pricesData || pricesData.length === 0) return 0;
    return pricesData.reduce((sum, item) => sum + (item.totalFoodPrice || 0), 0);
  };

  const getTotalDeliveryPrice = () => {
    if (!pricesData || pricesData.length === 0) return 0;
    return pricesData.reduce((sum, item) => sum + (item.totalDeliveryPrice || 0), 0);
  };

  const getTotalPlatformProfit = () => {
    if (!pricesData || pricesData.length === 0) return 0;
    return pricesData.reduce((sum, item) => sum + (item.totalPlatformProfit || 0), 0);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US').format(price);
  };

  // Prepare restaurant chart data
  const restaurantChartData = (restaurantData || []).map(item => ({
    date: formatDate(item.date),
    ordersCount: item.ordersCount || 0,
    foodPrice: item.totalFoodPrice || 0,
    profit: item.restaurantProfit || 0
  }));

  const getTotalRestaurantOrders = () => {
    if (!restaurantData || restaurantData.length === 0) return 0;
    return restaurantData.reduce((sum, item) => sum + (item.ordersCount || 0), 0);
  };

  const getTotalRestaurantFoodPrice = () => {
    if (!restaurantData || restaurantData.length === 0) return 0;
    return restaurantData.reduce((sum, item) => sum + (item.totalFoodPrice || 0), 0);
  };

  const getTotalRestaurantProfit = () => {
    if (!restaurantData || restaurantData.length === 0) return 0;
    return restaurantData.reduce((sum, item) => sum + (item.restaurantProfit || 0), 0);
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>{t('statistics.title')}</h1>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {/* Orders Line Graph */}
        <div className="detail-card full-width">
          <h3 style={{ marginBottom: '1rem' }}>{t('statistics.ordersPerDay')}</h3>

          {/* Date Range Presets */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginBottom: '1rem'
            }}>
              {(['today', 'last7days', 'thisWeek', 'thisMonth', 'last30days', 'custom'] as DateRangePreset[]).map((preset) => (
                <button
                  key={preset}
                  onClick={() => handlePresetChange(preset)}
                  disabled={loading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: `2px solid ${selectedPreset === preset ? '#007bff' : '#ddd'}`,
                    borderRadius: '6px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    backgroundColor: selectedPreset === preset ? '#007bff' : '#fff',
                    color: selectedPreset === preset ? '#fff' : '#333',
                    fontWeight: selectedPreset === preset ? 600 : 500,
                    transition: 'all 0.2s ease',
                    opacity: loading ? 0.6 : 1,
                    fontSize: '0.95rem'
                  }}
                >
                  {getPresetLabel(preset)}
                </button>
              ))}
            </div>

            {/* Custom Date Range Pickers */}
            {selectedPreset === 'custom' && (
              <div style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: '#333',
                    fontSize: '0.9rem'
                  }}>
                    {t('statistics.fromDate')}
                  </label>
                  <input
                    type="date"
                    value={customFromDate}
                    onChange={(e) => setCustomFromDate(e.target.value)}
                    disabled={loading}
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: '#333',
                    fontSize: '0.9rem'
                  }}>
                    {t('statistics.toDate')}
                  </label>
                  <input
                    type="date"
                    value={customToDate}
                    onChange={(e) => setCustomToDate(e.target.value)}
                    disabled={loading}
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              {t('statistics.loading')}
            </div>
          ) : data.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              {t('statistics.noData')}
            </div>
          ) : (
            <div style={{ width: '100%', height: 450, marginTop: '1.5rem', minHeight: 450 }}>
              <ResponsiveContainer width="100%" height={450}>
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="date"
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '10px'
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="orders"
                    stroke="#007bff"
                    strokeWidth={3}
                    dot={{ fill: '#007bff', r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('statistics.completedOrders')}
                  />
                  <Line
                    type="monotone"
                    dataKey="problematicallyCompleted"
                    stroke="#ffc107"
                    strokeWidth={3}
                    dot={{ fill: '#ffc107', r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('statistics.problematicallyCompletedOrders')}
                  />
                  <Line
                    type="monotone"
                    dataKey="cancelled"
                    stroke="#dc3545"
                    strokeWidth={3}
                    dot={{ fill: '#dc3545', r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('statistics.cancelledOrders')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Summary Cards */}
          {!loading && data.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap'
            }}>
              <div style={{
                backgroundColor: '#e7f3ff',
                borderLeft: '4px solid #007bff',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.totalOrders')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff' }}>
                  {getTotalOrders()}
                </div>
              </div>

              <div style={{
                backgroundColor: '#fff3cd',
                borderLeft: '4px solid #ffc107',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.averagePerDay')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffc107' }}>
                  {getAverageOrders()}
                </div>
              </div>

              <div style={{
                backgroundColor: '#d4edda',
                borderLeft: '4px solid #28a745',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.daysTracked')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                  {data.length}
                </div>
              </div>

              <div style={{
                backgroundColor: '#fff9e6',
                borderLeft: '4px solid #ffc107',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.totalProblematicallyCompleted')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffc107' }}>
                  {getTotalProblematicallyCompleted()}
                </div>
              </div>

              <div style={{
                backgroundColor: '#f8d7da',
                borderLeft: '4px solid #dc3545',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.totalCancelled')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#dc3545' }}>
                  {getTotalCancelled()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Total Clients Line Graph */}
        <div className="detail-card full-width" style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>{t('statistics.totalClientsPerDay')}</h3>

          {/* Date Range Presets */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginBottom: '1rem'
            }}>
              {(['today', 'last7days', 'thisWeek', 'thisMonth', 'last30days', 'custom'] as DateRangePreset[]).map((preset) => (
                <button
                  key={preset}
                  onClick={() => handleClientsPresetChange(preset)}
                  disabled={clientsLoading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: `2px solid ${clientsSelectedPreset === preset ? '#28a745' : '#ddd'}`,
                    borderRadius: '6px',
                    cursor: clientsLoading ? 'not-allowed' : 'pointer',
                    backgroundColor: clientsSelectedPreset === preset ? '#28a745' : '#fff',
                    color: clientsSelectedPreset === preset ? '#fff' : '#333',
                    fontWeight: clientsSelectedPreset === preset ? 600 : 500,
                    transition: 'all 0.2s ease',
                    opacity: clientsLoading ? 0.6 : 1,
                    fontSize: '0.95rem'
                  }}
                >
                  {getPresetLabel(preset)}
                </button>
              ))}
            </div>

            {/* Custom Date Range Pickers */}
            {clientsSelectedPreset === 'custom' && (
              <div style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: '#333',
                    fontSize: '0.9rem'
                  }}>
                    {t('statistics.fromDate')}
                  </label>
                  <input
                    type="date"
                    value={clientsCustomFromDate}
                    onChange={(e) => setClientsCustomFromDate(e.target.value)}
                    disabled={clientsLoading}
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      cursor: clientsLoading ? 'not-allowed' : 'pointer'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: '#333',
                    fontSize: '0.9rem'
                  }}>
                    {t('statistics.toDate')}
                  </label>
                  <input
                    type="date"
                    value={clientsCustomToDate}
                    onChange={(e) => setClientsCustomToDate(e.target.value)}
                    disabled={clientsLoading}
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      cursor: clientsLoading ? 'not-allowed' : 'pointer'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {clientsError && (
            <div className="error-message" style={{ marginBottom: '1.5rem' }}>
              {clientsError}
            </div>
          )}

          {clientsLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              {t('statistics.loading')}
            </div>
          ) : clientsData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              {t('statistics.noData')}
            </div>
          ) : (
            <div style={{ width: '100%', height: 450, marginTop: '1.5rem', minHeight: 450 }}>
              <ResponsiveContainer width="100%" height={450}>
                <LineChart
                  data={clientsChartData}
                  margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="date"
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '10px'
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="ios"
                    stroke="#007AFF"
                    strokeWidth={3}
                    dot={{ fill: '#007AFF', r: 5 }}
                    activeDot={{ r: 7 }}
                    name="iOS Clients"
                  />
                  <Line
                    type="monotone"
                    dataKey="android"
                    stroke="#3DDC84"
                    strokeWidth={3}
                    dot={{ fill: '#3DDC84', r: 5 }}
                    activeDot={{ r: 7 }}
                    name="Android Clients"
                  />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="#28a745"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: '#28a745', r: 4 }}
                    activeDot={{ r: 6 }}
                    name={t('statistics.totalClients')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Summary Cards */}
          {!clientsLoading && clientsData.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap'
            }}>
              <div style={{
                backgroundColor: '#d4edda',
                borderLeft: '4px solid #28a745',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.totalClients')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                  {getTotalClients()}
                </div>
              </div>

              <div style={{
                backgroundColor: '#e7f3ff',
                borderLeft: '4px solid #007AFF',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  iOS Clients
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007AFF' }}>
                  {getTotalIOSClients()}
                </div>
              </div>

              <div style={{
                backgroundColor: '#d7f5e3',
                borderLeft: '4px solid #3DDC84',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  Android Clients
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3DDC84' }}>
                  {getTotalAndroidClients()}
                </div>
              </div>

              <div style={{
                backgroundColor: '#fff3e6',
                borderLeft: '4px solid #ff9800',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.newClients')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ff9800' }}>
                  {getNewClients()}
                </div>
              </div>

              <div style={{
                backgroundColor: '#fff3cd',
                borderLeft: '4px solid #ffc107',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.averagePerDay')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffc107' }}>
                  {getAverageClientsPerDay()}
                </div>
              </div>

              <div style={{
                backgroundColor: '#f8f9fa',
                borderLeft: '4px solid #6c757d',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.daysTracked')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6c757d' }}>
                  {clientsData.length}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Ordered Clients Line Graph */}
        <div className="detail-card full-width" style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>{t('statistics.orderedClients')}</h3>

          {/* Date Range Presets */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginBottom: '1rem'
            }}>
              {(['today', 'last7days', 'thisWeek', 'thisMonth', 'last30days', 'custom'] as DateRangePreset[]).map((preset) => (
                <button
                  key={preset}
                  onClick={() => handleOrderedClientsPresetChange(preset)}
                  disabled={orderedClientsLoading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: `2px solid ${orderedClientsSelectedPreset === preset ? '#6f42c1' : '#ddd'}`,
                    borderRadius: '6px',
                    cursor: orderedClientsLoading ? 'not-allowed' : 'pointer',
                    backgroundColor: orderedClientsSelectedPreset === preset ? '#6f42c1' : '#fff',
                    color: orderedClientsSelectedPreset === preset ? '#fff' : '#333',
                    fontWeight: orderedClientsSelectedPreset === preset ? 600 : 500,
                    transition: 'all 0.2s ease',
                    opacity: orderedClientsLoading ? 0.6 : 1,
                    fontSize: '0.95rem'
                  }}
                >
                  {getPresetLabel(preset)}
                </button>
              ))}
            </div>

            {/* Custom Date Range Pickers */}
            {orderedClientsSelectedPreset === 'custom' && (
              <div style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: '#333',
                    fontSize: '0.9rem'
                  }}>
                    {t('statistics.fromDate')}
                  </label>
                  <input
                    type="date"
                    value={orderedClientsCustomFromDate}
                    onChange={(e) => setOrderedClientsCustomFromDate(e.target.value)}
                    disabled={orderedClientsLoading}
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      cursor: orderedClientsLoading ? 'not-allowed' : 'pointer'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: '#333',
                    fontSize: '0.9rem'
                  }}>
                    {t('statistics.toDate')}
                  </label>
                  <input
                    type="date"
                    value={orderedClientsCustomToDate}
                    onChange={(e) => setOrderedClientsCustomToDate(e.target.value)}
                    disabled={orderedClientsLoading}
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      cursor: orderedClientsLoading ? 'not-allowed' : 'pointer'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {orderedClientsError && (
            <div className="error-message" style={{ marginBottom: '1.5rem' }}>
              {orderedClientsError}
            </div>
          )}

          {orderedClientsLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              {t('statistics.loading')}
            </div>
          ) : orderedClientsData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              {t('statistics.noData')}
            </div>
          ) : (
            <div style={{ width: '100%', height: 450, marginTop: '1.5rem', minHeight: 450 }}>
              <ResponsiveContainer width="100%" height={450}>
                <LineChart
                  data={orderedClientsChartData}
                  margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="date"
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '10px'
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="allOrdered"
                    stroke="#6f42c1"
                    strokeWidth={3}
                    dot={{ fill: '#6f42c1', r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('statistics.allOrderedClients')}
                  />
                  <Line
                    type="monotone"
                    dataKey="orderedTwice"
                    stroke="#007bff"
                    strokeWidth={3}
                    dot={{ fill: '#007bff', r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('statistics.clientsOrderedTwice')}
                  />
                  <Line
                    type="monotone"
                    dataKey="orderedMoreThanTwice"
                    stroke="#28a745"
                    strokeWidth={3}
                    dot={{ fill: '#28a745', r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('statistics.clientsOrderedMoreThanTwice')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Summary Cards */}
          {!orderedClientsLoading && orderedClientsData.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap'
            }}>
              <div style={{
                backgroundColor: '#e7e3f7',
                borderLeft: '4px solid #6f42c1',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.allOrderedClients')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6f42c1' }}>
                  {orderedClientsData[orderedClientsData.length - 1]?.allOrderedClientsCount || 0}
                </div>
              </div>

              <div style={{
                backgroundColor: '#e7f3ff',
                borderLeft: '4px solid #007bff',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.clientsOrderedTwice')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff' }}>
                  {orderedClientsData[orderedClientsData.length - 1]?.clientsOrderedTwiceCount || 0}
                </div>
              </div>

              <div style={{
                backgroundColor: '#d4edda',
                borderLeft: '4px solid #28a745',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.clientsOrderedMoreThanTwice')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                  {orderedClientsData[orderedClientsData.length - 1]?.clientsOrderedMoreThanTwiceCount || 0}
                </div>
              </div>

              <div style={{
                backgroundColor: '#fff3cd',
                borderLeft: '4px solid #ffc107',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.daysTracked')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffc107' }}>
                  {orderedClientsData.length}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Prices Per Day Line Graph */}
        <div className="detail-card full-width" style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>{t('statistics.pricesPerDay')}</h3>

          {/* Date Range Presets */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginBottom: '1rem'
            }}>
              {(['today', 'last7days', 'thisWeek', 'thisMonth', 'last30days', 'custom'] as DateRangePreset[]).map((preset) => (
                <button
                  key={preset}
                  onClick={() => handlePricesPresetChange(preset)}
                  disabled={pricesLoading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: `2px solid ${pricesSelectedPreset === preset ? '#6f42c1' : '#ddd'}`,
                    borderRadius: '6px',
                    cursor: pricesLoading ? 'not-allowed' : 'pointer',
                    backgroundColor: pricesSelectedPreset === preset ? '#6f42c1' : '#fff',
                    color: pricesSelectedPreset === preset ? '#fff' : '#333',
                    fontWeight: pricesSelectedPreset === preset ? 600 : 500,
                    transition: 'all 0.2s ease',
                    opacity: pricesLoading ? 0.6 : 1,
                    fontSize: '0.95rem'
                  }}
                >
                  {getPresetLabel(preset)}
                </button>
              ))}
            </div>

            {/* Custom Date Range Pickers */}
            {pricesSelectedPreset === 'custom' && (
              <div style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: '#333',
                    fontSize: '0.9rem'
                  }}>
                    {t('statistics.fromDate')}
                  </label>
                  <input
                    type="date"
                    value={pricesCustomFromDate}
                    onChange={(e) => setPricesCustomFromDate(e.target.value)}
                    disabled={pricesLoading}
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      cursor: pricesLoading ? 'not-allowed' : 'pointer'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: '#333',
                    fontSize: '0.9rem'
                  }}>
                    {t('statistics.toDate')}
                  </label>
                  <input
                    type="date"
                    value={pricesCustomToDate}
                    onChange={(e) => setPricesCustomToDate(e.target.value)}
                    disabled={pricesLoading}
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      cursor: pricesLoading ? 'not-allowed' : 'pointer'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {pricesError && (
            <div className="error-message" style={{ marginBottom: '1.5rem' }}>
              {pricesError}
            </div>
          )}

          {pricesLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              {t('statistics.loading')}
            </div>
          ) : pricesData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              {t('statistics.noData')}
            </div>
          ) : (
            <div style={{ width: '100%', height: 450, marginTop: '1.5rem', minHeight: 450 }}>
              <ResponsiveContainer width="100%" height={450}>
                <LineChart
                  data={pricesChartData}
                  margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="date"
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '10px'
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="foodPrice"
                    stroke="#28a745"
                    strokeWidth={3}
                    dot={{ fill: '#28a745', r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('statistics.foodPrice')}
                  />
                  <Line
                    type="monotone"
                    dataKey="deliveryPrice"
                    stroke="#007bff"
                    strokeWidth={3}
                    dot={{ fill: '#007bff', r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('statistics.deliveryPrice')}
                  />
                  <Line
                    type="monotone"
                    dataKey="profit"
                    stroke="#ffc107"
                    strokeWidth={3}
                    dot={{ fill: '#ffc107', r: 5 }}
                    activeDot={{ r: 7 }}
                    name={t('statistics.platformProfit')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Summary Cards */}
          {!pricesLoading && pricesData.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap'
            }}>
              <div style={{
                backgroundColor: '#d4edda',
                borderLeft: '4px solid #28a745',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.totalFoodPrice')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                  {formatPrice(getTotalFoodPrice())}
                </div>
              </div>

              <div style={{
                backgroundColor: '#e7f3ff',
                borderLeft: '4px solid #007bff',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.totalDeliveryPrice')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff' }}>
                  {formatPrice(getTotalDeliveryPrice())}
                </div>
              </div>

              <div style={{
                backgroundColor: '#fff3cd',
                borderLeft: '4px solid #ffc107',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.totalPlatformProfit')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffc107' }}>
                  {formatPrice(getTotalPlatformProfit())}
                </div>
              </div>

              <div style={{
                backgroundColor: '#d4edda',
                borderLeft: '4px solid #28a745',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.daysTracked')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                  {pricesData.length}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Restaurant Statistics */}
        <div className="detail-card full-width" style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>{t('statistics.restaurantStatistics')}</h3>

          {/* Restaurant Selector */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 600,
              color: '#333'
            }}>
              {t('statistics.selectRestaurant')}
            </label>
            <select
              value={selectedRestaurantId}
              onChange={handleRestaurantChange}
              disabled={restaurantsLoading}
              style={{
                width: '100%',
                maxWidth: '400px',
                padding: '0.75rem',
                border: '2px solid #ddd',
                borderRadius: '6px',
                fontSize: '1rem',
                backgroundColor: '#fff',
                cursor: restaurantsLoading ? 'not-allowed' : 'pointer'
              }}
            >
              <option value="">
                {restaurantsLoading ? t('common.loading') : t('statistics.chooseRestaurant')}
              </option>
              {restaurants.map((restaurant) => (
                <option key={restaurant.id} value={restaurant.id.toString()}>
                  {restaurant.name}
                </option>
              ))}
            </select>
          </div>

          {selectedRestaurantId && (
            <>
              {/* Date Range Presets */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  display: 'flex',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                  marginBottom: '1rem'
                }}>
                  {(['today', 'last7days', 'thisWeek', 'thisMonth', 'last30days', 'custom'] as DateRangePreset[]).map((preset) => (
                    <button
                      key={preset}
                      onClick={() => handleRestaurantPresetChange(preset)}
                      disabled={restaurantLoading}
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: `2px solid ${restaurantSelectedPreset === preset ? '#17a2b8' : '#ddd'}`,
                        borderRadius: '6px',
                        cursor: restaurantLoading ? 'not-allowed' : 'pointer',
                        backgroundColor: restaurantSelectedPreset === preset ? '#17a2b8' : '#fff',
                        color: restaurantSelectedPreset === preset ? '#fff' : '#333',
                        fontWeight: restaurantSelectedPreset === preset ? 600 : 500,
                        transition: 'all 0.2s ease',
                        opacity: restaurantLoading ? 0.6 : 1,
                        fontSize: '0.95rem'
                      }}
                    >
                      {getPresetLabel(preset)}
                    </button>
                  ))}
                </div>

                {/* Custom Date Range Pickers */}
                {restaurantSelectedPreset === 'custom' && (
                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: 600,
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        {t('statistics.fromDate')}
                      </label>
                      <input
                        type="date"
                        value={restaurantCustomFromDate}
                        onChange={(e) => setRestaurantCustomFromDate(e.target.value)}
                        disabled={restaurantLoading}
                        style={{
                          padding: '0.75rem',
                          border: '2px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          backgroundColor: '#fff',
                          cursor: restaurantLoading ? 'not-allowed' : 'pointer'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: 600,
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        {t('statistics.toDate')}
                      </label>
                      <input
                        type="date"
                        value={restaurantCustomToDate}
                        onChange={(e) => setRestaurantCustomToDate(e.target.value)}
                        disabled={restaurantLoading}
                        style={{
                          padding: '0.75rem',
                          border: '2px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          backgroundColor: '#fff',
                          cursor: restaurantLoading ? 'not-allowed' : 'pointer'
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {restaurantError && (
                <div className="error-message" style={{ marginBottom: '1.5rem' }}>
                  {restaurantError}
                </div>
              )}

              {restaurantLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                  {t('statistics.loading')}
                </div>
              ) : restaurantData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                  {t('statistics.noData')}
                </div>
              ) : (
                <div style={{ width: '100%', height: 450, marginTop: '1.5rem', minHeight: 450 }}>
                  <ResponsiveContainer width="100%" height={450}>
                    <LineChart
                      data={restaurantChartData}
                      margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis
                        dataKey="date"
                        stroke="#666"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis
                        stroke="#666"
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          padding: '10px'
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="ordersCount"
                        stroke="#007bff"
                        strokeWidth={3}
                        dot={{ fill: '#007bff', r: 5 }}
                        activeDot={{ r: 7 }}
                        name={t('statistics.ordersCount')}
                      />
                      <Line
                        type="monotone"
                        dataKey="foodPrice"
                        stroke="#28a745"
                        strokeWidth={3}
                        dot={{ fill: '#28a745', r: 5 }}
                        activeDot={{ r: 7 }}
                        name={t('statistics.foodPrice')}
                      />
                      <Line
                        type="monotone"
                        dataKey="profit"
                        stroke="#17a2b8"
                        strokeWidth={3}
                        dot={{ fill: '#17a2b8', r: 5 }}
                        activeDot={{ r: 7 }}
                        name={t('statistics.restaurantProfit')}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Summary Cards */}
              {!restaurantLoading && restaurantData.length > 0 && (
                <div style={{
                  display: 'flex',
                  gap: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  <div style={{
                    backgroundColor: '#e7f3ff',
                    borderLeft: '4px solid #007bff',
                    padding: '0.75rem 1rem',
                    borderRadius: '4px',
                    flex: '1 1 auto',
                    minWidth: '140px'
                  }}>
                    <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                      {t('statistics.totalRestaurantOrders')}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff' }}>
                      {getTotalRestaurantOrders()}
                    </div>
                  </div>

                  <div style={{
                    backgroundColor: '#d4edda',
                    borderLeft: '4px solid #28a745',
                    padding: '0.75rem 1rem',
                    borderRadius: '4px',
                    flex: '1 1 auto',
                    minWidth: '140px'
                  }}>
                    <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                      {t('statistics.totalRestaurantFoodPrice')}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                      {formatPrice(getTotalRestaurantFoodPrice())}
                    </div>
                  </div>

                  <div style={{
                    backgroundColor: '#d1ecf1',
                    borderLeft: '4px solid #17a2b8',
                    padding: '0.75rem 1rem',
                    borderRadius: '4px',
                    flex: '1 1 auto',
                    minWidth: '140px'
                  }}>
                    <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                      {t('statistics.totalRestaurantProfit')}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#17a2b8' }}>
                      {formatPrice(getTotalRestaurantProfit())}
                    </div>
                  </div>

                  <div style={{
                    backgroundColor: '#fff3cd',
                    borderLeft: '4px solid #ffc107',
                    padding: '0.75rem 1rem',
                    borderRadius: '4px',
                    flex: '1 1 auto',
                    minWidth: '140px'
                  }}>
                    <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                      {t('statistics.daysTracked')}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffc107' }}>
                      {restaurantData.length}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Driver Order Statistics */}
        <div className="detail-card full-width" style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>{t('statistics.driverOrderStatistics')}</h3>

          {/* Controls */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: '#333'
              }}>
                {t('statistics.numberOfDrivers')}
              </label>
              <input
                type="number"
                value={driversCount}
                onChange={handleDriversCountChange}
                min="1"
                max="50"
                disabled={driverLoading}
                style={{
                  width: '200px',
                  padding: '0.75rem',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  backgroundColor: '#fff',
                  cursor: driverLoading ? 'not-allowed' : 'text'
                }}
              />
            </div>

            {/* Date Range Presets */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginBottom: '1rem'
            }}>
              {(['today', 'last7days', 'thisWeek', 'thisMonth', 'last30days', 'custom'] as DateRangePreset[]).map((preset) => (
                <button
                  key={preset}
                  onClick={() => handleDriverPresetChange(preset)}
                  disabled={driverLoading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: `2px solid ${driverSelectedPreset === preset ? '#dc3545' : '#ddd'}`,
                    borderRadius: '6px',
                    cursor: driverLoading ? 'not-allowed' : 'pointer',
                    backgroundColor: driverSelectedPreset === preset ? '#dc3545' : '#fff',
                    color: driverSelectedPreset === preset ? '#fff' : '#333',
                    fontWeight: driverSelectedPreset === preset ? 600 : 500,
                    transition: 'all 0.2s ease',
                    opacity: driverLoading ? 0.6 : 1,
                    fontSize: '0.95rem'
                  }}
                >
                  {getPresetLabel(preset)}
                </button>
              ))}
            </div>

            {/* Custom Date Range Pickers */}
            {driverSelectedPreset === 'custom' && (
              <div style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: '#333',
                    fontSize: '0.9rem'
                  }}>
                    {t('statistics.fromDate')}
                  </label>
                  <input
                    type="date"
                    value={driverCustomFromDate}
                    onChange={(e) => setDriverCustomFromDate(e.target.value)}
                    disabled={driverLoading}
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      cursor: driverLoading ? 'not-allowed' : 'pointer'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: '#333',
                    fontSize: '0.9rem'
                  }}>
                    {t('statistics.toDate')}
                  </label>
                  <input
                    type="date"
                    value={driverCustomToDate}
                    onChange={(e) => setDriverCustomToDate(e.target.value)}
                    disabled={driverLoading}
                    style={{
                      padding: '0.75rem',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      cursor: driverLoading ? 'not-allowed' : 'pointer'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {driverError && (
            <div className="error-message" style={{ marginBottom: '1.5rem' }}>
              {driverError}
            </div>
          )}

          {driverLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              {t('statistics.loading')}
            </div>
          ) : driverData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              {t('statistics.noData')}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginTop: '1rem'
              }}>
                <thead>
                  <tr style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#333'
                    }}>
                      {t('statistics.rank')}
                    </th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#333'
                    }}>
                      {t('statistics.driverName')}
                    </th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#333'
                    }}>
                      {t('statistics.driverPhone')}
                    </th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'right',
                      fontWeight: 600,
                      color: '#333'
                    }}>
                      {t('statistics.ordersCount')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {driverData.map((driver, index) => (
                    <tr
                      key={driver.driverId}
                      style={{
                        borderBottom: '1px solid #dee2e6',
                        backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa'
                      }}
                    >
                      <td style={{
                        padding: '1rem',
                        fontWeight: index < 3 ? 'bold' : 'normal',
                        color: index === 0 ? '#ffc107' : index === 1 ? '#6c757d' : index === 2 ? '#cd7f32' : '#333'
                      }}>
                        {index + 1}
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        {driver.driverName}
                      </td>
                      <td style={{ padding: '1rem', color: '#666' }}>
                        {driver.driverPhone}
                      </td>
                      <td style={{
                        padding: '1rem',
                        textAlign: 'right',
                        fontWeight: 600,
                        color: '#007bff'
                      }}>
                        {driver.ordersCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Summary Card */}
          {!driverLoading && driverData.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginTop: '1.5rem'
            }}>
              <div style={{
                backgroundColor: '#f8d7da',
                borderLeft: '4px solid #dc3545',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.topPerformers')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#dc3545' }}>
                  {driverData.length}
                </div>
              </div>

              <div style={{
                backgroundColor: '#e7f3ff',
                borderLeft: '4px solid #007bff',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                flex: '1 1 auto',
                minWidth: '140px'
              }}>
                <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {t('statistics.totalOrdersByTopDrivers')}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff' }}>
                  {driverData.reduce((sum, driver) => sum + driver.ordersCount, 0)}
                </div>
              </div>

              {driverData.length > 0 && (
                <div style={{
                  backgroundColor: '#fff3cd',
                  borderLeft: '4px solid #ffc107',
                  padding: '0.75rem 1rem',
                  borderRadius: '4px',
                  flex: '1 1 auto',
                  minWidth: '140px'
                }}>
                  <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    {t('statistics.topDriver')}
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffc107' }}>
                    {driverData[0].driverName}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Statistics;
